import { formatDateInverseFast } from "@impero-com/common/utils/formatting";
import { assertNever } from "@impero-com/common/utils/obj";
import moment from "moment-timezone";

import {
    InitialFieldType,
    SubsequentFieldType,
    DefaultOrCustomForDueDateOffsetForMonthOffsetPeriod as DefaultOrCustomOffset,
    HalfYear,
    Month,
    Quarter,
    TwoMonths,
    ControlRecurrenceRule,
    DueDateOffsetForMonthOffsetPeriod as MonthDueDateOffset,
    ControlWithFieldsView,
} from "api/generated/model";
import { FieldCondition, Field } from "components/fieldBuilder/models";
import { keyToCoveredPeriod } from "utils/coveredPeriod";
import { generate } from "utils/guid";

import { ValidControlRow, TaskType, ImportedControlDefaults } from "./types";

const DEFAULT_TIMEZONE = "Europe/Copenhagen";
const DEFAULT_OFFSET_ANCHOR = "EndOfInterval";
const DEFAULT_OFFSET_TYPE = "AfterAnchor";

/**
 * Converts validated rows from a bulk import spreadsheet into control graph structures
 * that can be persisted to the database
 *
 * @param rows - The rows from the import spreadsheet
 * @param defaults - Values for the fields the spreadsheet has no column for
 * @returns Array of control graph structures ready for submission
 */
export function convertToControlGraphs(
    rows: ValidControlRow[],
    defaults: ImportedControlDefaults
): ControlWithFieldsView[] {
    return rows.map((row) => convertRowToControlGraph(row, defaults));
}

/**
 * Converts a single control row to a control graph structure
 *
 * @param row - Validated control row from the import spreadsheet
 * @param defaults - Values for the fields the spreadsheet has no column for
 * @returns A complete control graph structure
 */
function convertRowToControlGraph(
    row: ValidControlRow,
    defaults: ImportedControlDefaults
): ControlWithFieldsView {
    const startDate = buildControlStartDate(row);

    const recurrenceRule = buildRecurrenceRule(row);

    const coveredPeriod = keyToCoveredPeriod(row.coveredPeriod);

    const responsibleFields = buildInitialFields(row.tasks);
    const firstReviewerFields = buildSubsequentFields(row.firstReviewerTasks);
    const secondReviewerFields = buildSubsequentFields(row.secondReviewerTasks);

    return {
        id: generate(),
        title: row.title,
        description: row.description || null,
        controlProgramId: row.controlProgram.id,
        scheduling: {
            rule: recurrenceRule,
            dueTime: "23:59",
            startDate: formatDateInverseFast(startDate.toDate()),
            sendTime: "07:00",
            timezoneName: DEFAULT_TIMEZONE,
            coveredPeriod,
            remindersAfter: [],
            remindersBefore: [],
        },
        tagIds: row.tags?.map((tag) => tag.id) ?? [],
        lastUpdated: null,
        stepGroup: {
            id: generate(),
            initialStep: {
                id: generate(),
                fieldGroup: {
                    id: generate(),
                    fields: responsibleFields,
                },
                assignment: {},
                allocatedTime: {
                    days: row.notificationSentDaysBefore
                        ? row.notificationSentDaysBefore
                        : 5,
                    microseconds: 0,
                },
            },
            subsequentSteps: [
                {
                    id: generate(),
                    fieldGroup: {
                        id: generate(),
                        fields: firstReviewerFields,
                    },
                    allocatedTime: {
                        days: 1,
                        microseconds: 0,
                    },
                    assignment: {},
                },
                {
                    id: generate(),
                    fieldGroup: {
                        id: generate(),
                        fields: secondReviewerFields,
                    },
                    allocatedTime: {
                        days: 1,
                        microseconds: 0,
                    },
                    assignment: {},
                },
            ],
            notificationRecipients: {},
            entityIds: {},
        },
        assignmentSettings: {
            tagIds: {},
            loginProtectionEnabled: row.loginRequired,
            segregationOfDutiesEnabled: defaults.segregationOfDutiesEnabled,
        },
        triggeredBy: row.triggeredBy ?? null,
        triggeredControlCount: 0,
    };
}

/**
 * Builds field array from tasks for the initial step (supports all field types)
 *
 * @param tasks - Array of tasks from the import spreadsheet
 * @returns Array of fields with properly set up conditions
 */
function buildInitialFields(
    tasks?: ValidControlRow["tasks"]
): Array<Field<InitialFieldType, string>> {
    return buildFieldsFromTasks<InitialFieldType>(tasks, buildInitialFieldType);
}

/**
 * Builds field array from tasks for reviewer steps (excludes DataSheet type)
 *
 * @param tasks - Array of tasks from the import spreadsheet
 * @returns Array of fields with properly set up conditions
 */
function buildSubsequentFields(
    tasks?: ValidControlRow["tasks"]
): Array<Field<SubsequentFieldType, string>> {
    return buildFieldsFromTasks<SubsequentFieldType>(
        tasks,
        buildSubsequentFieldType
    );
}

/**
 * Internal helper function that builds fields from tasks with proper typing
 *
 * @param tasks - Array of tasks from the import spreadsheet
 * @param fieldTypeBuilder - Function to convert task type to appropriate field type
 * @returns Array of properly typed fields with conditions set up
 */
function buildFieldsFromTasks<T extends InitialFieldType | SubsequentFieldType>(
    tasks: ValidControlRow["tasks"],
    fieldTypeBuilder: (type: TaskType, options: string[]) => T
): Array<Field<T, string>> {
    interface FieldsAccumulator {
        fields: Array<Field<T, string>>;
        fieldsArray: Array<Field<T, string>>;
    }

    const { fields } = (tasks || []).reduce(
        (acc: FieldsAccumulator, task, index) => {
            try {
                const fieldType = fieldTypeBuilder(task.type, task.options);

                const newField: Field<T, string> = {
                    id: generate(),
                    title: task.title,
                    instructions: task.instructions,
                    fieldType,
                    blobIds: [],
                    mandatory: task.required === true,
                    condition: null as FieldCondition<string> | null,
                };

                if (task.condition) {
                    // Convert to 0-based index
                    const refTaskIndex = task.condition.refTaskIndex - 1;

                    // We can only reference fields that have already been processed
                    if (refTaskIndex >= 0 && refTaskIndex < index) {
                        const refField = acc.fieldsArray[refTaskIndex];

                        newField.condition = {
                            operator: task.condition.operator,
                            value: task.condition.value,
                            action: task.condition.action,
                            fieldId: refField.id,
                        };
                    }
                }

                acc.fieldsArray.push(newField);
                acc.fields.push(newField);
            } catch (error) {
                throw new Error(
                    `Invalid task type "${
                        task.type
                    }" at index ${index}. This should not happen at this stage. Original error: ${
                        error instanceof Error ? error.message : String(error)
                    }`
                );
            }

            return acc;
        },
        {
            fields: [],
            fieldsArray: [],
        }
    );

    return fields;
}

// Determines the appropriate start date for a control based on its recurrence type
function buildControlStartDate(row: ValidControlRow): moment.Moment {
    if (row.recurrenceType === "Date" && "dateRecurrenceDay" in row) {
        return moment(row.dateRecurrenceDay, "YYYY-MM-DD", true);
    }

    if (row.recurrenceStartDate) {
        return moment(row.recurrenceStartDate, "YYYY-MM-DD", true);
    }

    return moment().tz(DEFAULT_TIMEZONE);
}

/**
/**
 * Builds a recurrence rule based on the type specified in the control row
 *
 * @param row - Control data from the import spreadsheet
 * @returns A typed recurrence rule structure
 * @throws Error for unknown recurrence types
 */
function buildRecurrenceRule(row: ValidControlRow): ControlRecurrenceRule {
    switch (row.recurrenceType) {
        case "Date":
            return { type: "Date" };

        case "Weekly": {
            return {
                type: "Weekly",
                periodicity: row.weeklyRecurrenceFrequency,
                days: row.weeklyRecurrenceDays,
            };
        }

        case "Monthly":
            return {
                type: "Monthly",
                defaultDueDateOffset: buildDefaultOffset(
                    row.recurrenceOffsetDays
                ),
                occurrences: createOccurrenceMap<Month>(
                    row.monthlyRecurrenceMonths
                ),
                calendarId: row.calendar?.id ?? null,
            };

        case "EveryTwoMonths":
            return {
                type: "EveryTwoMonths",
                defaultDueDateOffset: buildDefaultOffset(
                    row.recurrenceOffsetDays
                ),
                occurrences: createOccurrenceMap<TwoMonths>(
                    row.everyTwoMonthsRecurrenceTwoMonths
                ),
                calendarId: row.calendar?.id ?? null,
            };

        case "Quarterly":
            return {
                type: "Quarterly",
                defaultDueDateOffset: buildDefaultOffset(
                    row.recurrenceOffsetDays
                ),
                calendarId: row.calendar?.id ?? null,
                financialYearId: null,
                occurrences: createOccurrenceMap<Quarter>(
                    row.quarterlyRecurrenceQuarters
                ),
            };

        case "HalfYearly":
            return {
                type: "HalfYearly",
                defaultDueDateOffset: buildDefaultOffset(
                    row.recurrenceOffsetDays
                ),
                calendarId: row.calendar?.id ?? null,
                financialYearId: null,
                occurrences: createOccurrenceMap<HalfYear>(
                    row.halfYearlyRecurrenceHalfYears
                ),
            };

        case "Yearly":
            return {
                type: "Yearly",
                defaultDueDateOffset: buildDefaultOffset(
                    row.recurrenceOffsetDays
                ),
                periodicity: 1,
                calendarId: row.calendar?.id ?? null,
                financialYearId: null,
            };

        default:
            return assertNever(row);
    }
}

/**
 * Builds a default offset configuration for recurrence rules
 *
 * @param offsetDays - Number of days to offset from the anchor point
 * @returns Configured offset structure
 */
function buildDefaultOffset(
    offsetDays: number | undefined
): MonthDueDateOffset {
    return {
        offsetPeriod: {
            months: 0,
            days: Number(offsetDays) - 1,
        },
        anchor: DEFAULT_OFFSET_ANCHOR,
        offsetType: DEFAULT_OFFSET_TYPE,
    };
}

/**
 * Creates a map of occurrences for various recurrence types
 *
 * @param occurrences - Array of occurrence values (e.g., months, quarters)
 * @returns A type-safe map of occurrences
 */
function createOccurrenceMap<T extends string>(
    occurrences: T[]
): Partial<Record<T, DefaultOrCustomOffset>> {
    return occurrences.reduce<Partial<Record<T, DefaultOrCustomOffset>>>(
        (acc, item) => {
            acc[item] = "Default";
            return acc;
        },
        {}
    );
}

// Builds a field type configuration for initial steps (supports all field types)
function buildInitialFieldType(
    type: TaskType,
    options: string[]
): InitialFieldType {
    return buildFieldType(type, options);
}

// Builds a field type configuration for subsequent steps (excludes DataSheet)
function buildSubsequentFieldType(
    type: TaskType,
    options: string[]
): SubsequentFieldType {
    const fieldType = buildFieldType(type, options);

    if (fieldType.type === "DataSheet") {
        throw new Error("DataSheet type is not supported in subsequent steps");
    }

    return fieldType;
}

/**
 * Builds a field type configuration based on the task type and options
 *
 * @param type - The type of task field to create
 * @param options - Options for field types that require them
 * @returns A configured field type structure
 * @throws Error for unknown field types
 */
function buildFieldType(
    type: TaskType,
    options: string[]
): InitialFieldType | SubsequentFieldType {
    switch (type) {
        case "TextInput":
            return { type: "TextInput" };
        case "TextArea":
            return { type: "TextArea" };
        case "NumericInput":
            return { type: "NumericInput" };
        case "FileUpload":
            return { type: "FileUpload" };
        case "Heading":
            return { type: "Heading" };
        case "RadioButtonGroup":
            return {
                type: "RadioButtonGroup",
                options,
            };
        case "CheckboxGroup":
            return {
                type: "CheckboxGroup",
                options,
            };
        case "Dropdown":
            return {
                type: "Dropdown",
                options,
            };
        case "DataSheet":
            return {
                type: "DataSheet",
                dataSheetId: generate(),
                grouping: [],
            };
        case "DataGrid":
            return {
                type: "DataGrid",
                initialNumberOfRows: 10,
                columns: [
                    { id: generate(), title: "Col1", type: "Text" },
                    { id: generate(), title: "Col2", type: "Text" },
                    { id: generate(), title: "Col3", type: "Text" },
                ],
            };
        default:
            return assertNever(type);
    }
}
